import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal, Button, Alert } from "react-bootstrap";
import { createPolicy, updatePolicy } from "../../redux/features/policySlice";
import { handleCompanyDetails } from "../../services/companies";
import "./PolicyForm.css";

const validationSchema = Yup.object().shape({
  policy_number: Yup.string().required("Policy number is required"),
  policy_holder_name: Yup.string().required("Policy holder name is required"),
  effective_date: Yup.date().required("Effective date is required"),
  expiration_date: Yup.date()
    .required("Expiration date is required")
    .min(
      Yup.ref("effective_date"),
      "Expiration date must be after effective date"
    ),
  law_firm_id: Yup.string(),
});

function PolicyForm({ policy, onClose, onSuccess, isAdmin = false }) {
  const dispatch = useDispatch();
  const [submitError, setSubmitError] = useState("");
  const [lawFirms, setLawFirms] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      fetchLawFirms();
    }
  }, [isAdmin]);

  const fetchLawFirms = async () => {
    try {
      const response = await handleCompanyDetails.getCompanies({
        limit: 1000,
      });
      setLawFirms(response.companies_data || []);
    } catch (error) {
      console.error("Error fetching law firms:", error);
      setLawFirms([]);
    }
  };

  // Helper function to format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const initialValues = policy
    ? {
        policy_number: policy.policy_number || "",
        policy_holder_name: policy.policy_holder_name || "",
        effective_date: formatDateForInput(policy.effective_date) || "",
        expiration_date: formatDateForInput(policy.expiration_date) || "",
        law_firm_id: policy.law_firm_id || "",
      }
    : {
        policy_number: "",
        policy_holder_name: "",
        effective_date: "",
        expiration_date: "",
        law_firm_id: "",
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitError("");
      const submitData = { ...values };
      if (policy) {
        await dispatch(
          updatePolicy({ id: policy.id, data: submitData })
        ).unwrap();
      } else {
        await dispatch(createPolicy(submitData)).unwrap();
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving policy:", error);
      setSubmitError(error.message || "Failed to save policy. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {policy ? "Edit Policy" : "Add New Policy"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {submitError && (
          <Alert variant="danger" onClose={() => setSubmitError("")} dismissible>
            {submitError}
          </Alert>
        )}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              {isAdmin && (
                <div className="form-group">
                  <label htmlFor="law_firm_id">Law Firm *</label>
                  <Field
                    as="select"
                    id="law_firm_id"
                    name="law_firm_id"
                    className="form-control"
                  >
                    <option value="">Select a law firm</option>
                    {lawFirms.map((firm) => (
                      <option key={firm.id} value={firm.id}>
                        {firm.title || firm.company_name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="law_firm_id" component="div" className="error-message" />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="policy_number">Policy Number *</label>
                <Field
                  type="text"
                  id="policy_number"
                  name="policy_number"
                  className="form-control"
                  placeholder="Enter policy number"
                  disabled={policy ? true : false}
                />
                <ErrorMessage name="policy_number" component="div" className="error-message" />
              </div>

              <div className="form-group">
                <label htmlFor="policy_holder_name">Policy Holder Name *</label>
                <Field
                  type="text"
                  id="policy_holder_name"
                  name="policy_holder_name"
                  className="form-control"
                  placeholder="Enter policy holder name"
                />
                <ErrorMessage name="policy_holder_name" component="div" className="error-message" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="effective_date">Effective Date *</label>
                  <Field
                    type="date"
                    id="effective_date"
                    name="effective_date"
                    className="form-control"
                  />
                  <ErrorMessage name="effective_date" component="div" className="error-message" />
                </div>

                <div className="form-group">
                  <label htmlFor="expiration_date">Expiration Date *</label>
                  <Field
                    type="date"
                    id="expiration_date"
                    name="expiration_date"
                    className="form-control"
                  />
                  <ErrorMessage name="expiration_date" component="div" className="error-message" />
                </div>
              </div>

              <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? "Saving..." : "Save Policy"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default PolicyForm;

